<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useMembersStore } from '@/stores/members'
import { useThemeStore, type ThemeChoice } from '@/stores/theme'
import { useCheckinStore } from '@/stores/checkin'

/**
 * Who you are, and the handful of things that are about you rather than
 * about the household: your settings, how the screen looks, and the way out.
 * The admin backend is here too when the flag is set, because it belongs to
 * the person rather than to any household (M-95).
 */
const auth = useAuthStore()
const members = useMembersStore()
const theme = useThemeStore()
const checkin = useCheckinStore()
const route = useRoute()

const open = ref(false)
const root = ref<HTMLElement | null>(null)
const avatar = ref<string | null>(null)

const me = computed(() => members.list.find((m) => m.id === auth.me?.memberId) ?? null)

const name = computed(() => me.value?.displayName ?? auth.me?.email ?? 'You')

/** Initials while there is no picture, which is most people most of the time. */
const initials = computed(() => {
  const from = me.value?.displayName ?? auth.me?.email ?? '?'
  return from
    .split(/[\s@.]+/)
    .filter(Boolean)
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase()
})

onMounted(async () => {
  document.addEventListener('click', onOutside)
  document.addEventListener('keydown', onKey)
  if (members.list.length === 0) await members.load().catch(() => undefined)
  await checkin.loadSettings().catch(() => undefined)
  if (me.value?.hasAvatar) avatar.value = await members.avatarUrl(me.value.id, 'thumb').catch(() => null)
})
onBeforeUnmount(() => {
  document.removeEventListener('click', onOutside)
  document.removeEventListener('keydown', onKey)
})

function onOutside(event: MouseEvent) {
  if (open.value && root.value && !root.value.contains(event.target as Node)) open.value = false
}
function onKey(event: KeyboardEvent) {
  if (event.key === 'Escape') open.value = false
}

// Going somewhere is the end of the menu's job.
watch(() => route.fullPath, () => (open.value = false))

const THEMES: Array<{ key: ThemeChoice; label: string }> = [
  { key: 'light', label: 'Light' },
  { key: 'dark', label: 'Dark' },
  { key: 'system', label: 'Auto' },
]
</script>

<template>
  <div ref="root" class="relative">
    <button
      class="flex h-9 w-9 items-center justify-center overflow-hidden rounded-full border border-stone bg-mist/40 text-xs font-medium text-ink/70"
      :aria-expanded="open"
      aria-haspopup="menu"
      :aria-label="`Account: ${name}`"
      @click="open = !open"
    >
      <img v-if="avatar" :src="avatar" alt="" class="h-full w-full object-cover" />
      <span v-else>{{ initials }}</span>
    </button>

    <div
      v-if="open"
      class="absolute right-0 z-50 mt-2 w-64 overflow-hidden rounded-xl border border-stone bg-surface shadow-xl"
      role="menu"
    >
      <div class="border-b border-stone px-4 py-3">
        <p class="truncate text-sm font-medium">{{ name }}</p>
        <p v-if="auth.me?.email" class="truncate text-xs text-ink/50">{{ auth.me.email }}</p>
      </div>

      <!-- Your own figures, by member id rather than a fixed path. Taken from
         the auth store rather than the members list, so the link is right
         even on the first paint, before that list has loaded. -->
      <RouterLink
        v-if="auth.me?.memberId"
        :to="`/people/${auth.me.memberId}/stats`"
        class="flex items-center px-4 py-2.5 text-sm hover:bg-mist/30"
        role="menuitem"
      >
        Statistics
      </RouterLink>

      <RouterLink
        to="/measurements"
        class="flex items-center px-4 py-2.5 text-sm hover:bg-mist/30"
        role="menuitem"
      >
        Measurements
      </RouterLink>

      <!-- Only when somebody has switched it on. Nobody is offered a place to
         record how they feel because a release landed (M-107). -->
      <RouterLink
        v-if="checkin.enabled"
        to="/check-in"
        class="flex items-center px-4 py-2.5 text-sm hover:bg-mist/30"
        role="menuitem"
      >
        How are you today?
      </RouterLink>

      <RouterLink
        to="/settings"
        class="flex items-center px-4 py-2.5 text-sm hover:bg-mist/30"
        role="menuitem"
      >
        My settings
      </RouterLink>

      <!-- Only a superuser has anywhere to go; the server refuses it regardless. -->
      <RouterLink
        v-if="auth.me?.isSuperuser"
        to="/admin/foods"
        class="flex items-center px-4 py-2.5 text-sm hover:bg-mist/30"
        role="menuitem"
      >
        Administration
      </RouterLink>

      <div class="border-t border-stone px-4 py-3">
        <p class="text-xs uppercase tracking-wide text-ink/40">Appearance</p>
        <div class="mt-1.5 flex rounded-xl border border-stone p-0.5 text-xs">
          <button
            v-for="option in THEMES"
            :key="option.key"
            class="flex-1 rounded-lg px-2 py-1.5"
            :class="theme.choice === option.key ? 'bg-moss font-medium text-paper' : 'text-ink/60'"
            :aria-pressed="theme.choice === option.key"
            @click="theme.set(option.key)"
          >
            {{ option.label }}
          </button>
        </div>
      </div>

      <button
        class="flex w-full items-center border-t border-stone px-4 py-2.5 text-left text-sm hover:bg-mist/30"
        role="menuitem"
        @click="auth.signOut()"
      >
        Sign out
      </button>
    </div>
  </div>
</template>
